import { DateTime } from 'luxon';
import { Enumeration } from 'src/common/types';
import {
  ClientNewReportsState,
  InitiateReportRequest,
  ReportGeneration,
  ReportResult,
  SaveReportingParametersRequest,
} from 'src/features/clients/client/reportsV2/store/types';
import { ClientItem } from 'src/features/clients/list/store/types';
import { PagedResult, QueryField } from 'src/store';
import { ReportingBatchState } from '../batch/store/types';
import { FromEmailAddressType, SchedulerState } from '../scheduler/store/types';

export interface ReportingState {
  reports: ClientNewReportsState;
  bulk: BulkReportingState;
  batch: ReportingBatchState;
  scheduler: SchedulerState;
}

export interface BulkReportingState {
  clientTypes: string[];
  serviceType: string;
  portfolioValueFrom: number | null;
  portfolioValueTo: number | null;
  availableCashBalanceFrom: number | null;
  availableCashBalanceTo: number | null;
  securityHeld: string;
  security: SecurityItem | null;
  securities: SecurityItem[];
  model: ModelItem | null;
  models: ModelItem[];
  afsls: AfslItem[];
  groups: GroupItem[];
  clients: ClientItem[];
  practices: PracticeItem[];
  advisers: AdviserItem[];
  selectedAfsls: AfslItem[];
  selectedGroups: GroupItem[];
  selectedPractices: PracticeItem[];
  selectedAdvisers: AdviserItem[];
  selectedClients: ClientItem[];
  clientSearchResults: ClientSearchResult[];
  selectedClientRows: number[];
  selectedClientIdRows: Set<number>;
  selectedGroupRows: number[];
  currentDraft?: BulkReportJob;
  paginatedBulkReportResults: BulkReportList;
  paginatedEventListResults: PagedResult<ScheduledEvent>;
  namingTemplate: PreDefinedTag[];
  allowTerminatedClient: boolean;
  reportGeneration: ReportGeneration;
  reportResult?: ReportResult | null;
  immediateBulkReport?: ImmediateBulkReportByIdViewModel;
  csvUploaded: boolean;
  reportingParametersList: PagedResult<GetReportParametersViewModel>;
}

export interface SetPreDefinedTagValue {
  index: number;
  value: string;
}

export interface PreDefinedTag {
  tag: string;
  example: string;
  value?: string;
}

export interface BulkReportRequest {
  draft?: boolean;
  scheduledOnly?: boolean;
  pageSize: number;
  page: number;
  queryFields: QueryField[];
}

export interface BulkReportList {
  pageNumber: number;
  pageSize: number;
  results: BulkReportJob[];
  totalNumberOfPages: number;
  totalNumberOfRecords: number;
}

export interface CreateBulkReportRequest {
  batchId?: string;
  name?: string;
  reportFileNameTemplate?: string;
  clientOrGroupIds?: number[];
  addToExistingClientOrGroupIds: boolean;
  reportParameters?: InitiateReportRequest;
  allowTerminatedClients?: boolean;
  runImmediately: boolean;
}

export interface SwapTagRequest {
  from: number;
  to: number;
}

export interface ImmediateBulkReportByIdViewModel {
  key?: string;
  status?: string;
  downloadFileName?: string;
}

export interface BulkReportJob {
  id: string;
  name: string;
  createdOn: DateTime;
  updatedOn: DateTime;
  createdById: number;
  reportFileNameTemplate: string;
  progress: BulkReportJobProgress;
  isDraft: boolean;
  reportParameters?: InitiateReportRequest;
  emailParameters?: EmailParametersRequest;
  scheduleInstanceDetails?: ScheduledInstanceDto;
}

export interface DownloadBulkReportsCsvRequest {
  selectedRows: number[];
}

export interface ScheduledInstanceDto {
  id: string;
  scheduledEventId: string;
  scheduledEventName?: string;
  failedDocumentUploadRecords: number;
  failedEmailSentRecords: number;
  runOn: Date;
  reviewStatus: string;
}

export interface BulkReportJobProgress {
  batchId: string;
  completedRecords: number;
  totalRecords: number;
}

export interface ReportJobUpdateModalForm {
  name: string;
  reportFileNameTemplate: string;
}

export interface ClientSearchParameters {
  afsls: AfslItem[];
  practices: PracticeItem[];
  advisers: AdviserItem[];
  clientTypes: string[];
  serviceType: string;
  securityHeld: string;
  statuses: string[];
  availableCashBalanceFrom?: number;
  availableCashBalanceTo?: number;
  portfolioValueFrom?: number;
  portfolioValueTo?: number;
  securityId?: number;
  modelId?: number;
  isDataFromTriumph?: boolean;
}

export class SecurityModelAssociationType extends Enumeration {
  static Security = new SecurityModelAssociationType(0, 'Security', 'Security');
  static Model = new SecurityModelAssociationType(1, 'Model', 'Model');
}

export interface ClientCsvRow {
  data: string[];
  index: number;
}

export interface ClientSearchResult {
  clientId: number;
  clientName: string;
  clientType: string;
}

export interface Client {
  id: number;
  name: string;
}

export interface AfslViewModel {
  afsls: AfslItem[];
}

export interface AfslItem {
  afslId: number;
  afslName: string;
  practices: PracticeItem[];
  groups: GroupItem[];
}

export interface GroupItem {
  groupId: number;
  groupName: string;
}

export interface PracticeItem {
  practiceId: number;
  practiceName: string;
  advisors: AdviserItem[];
}

export interface AdviserItem {
  advisorId: number;
  advisorName: string;
}

export interface SecurityItem {
  securityId: number;
  securityCode: string;
  securityName: string;
}

export interface ModelItem {
  modelId: number;
  modelVersionId: number;
  code: string;
  name: string;
  version: string;
  type: string;
  status: string;
  method: string;
  description: string;
  oneMonthReturn?: number;
}

export interface EventRecurrenceRule {
  id: string;
  interval?: number;
  daysOfWeek?: string[];
  dayOfMonth?: number;
  weekOfMonth?: number;
  monthOfYear?: number;
}

export type RecurrenceRule = {
  id?: string;
  interval?: number | null;
  daysOfWeek?: string[] | null;
  dayOfMonth?: number | null;
  weekOfMonth?: number | null;
  monthOfYear?: number | null;
};

export interface ScheduledEvent {
  id: string;
  name?: string;
  description?: string;
  startOn: Date;
  recurrenceType?: RecurrenceType;
  recurrenceRule?: EventRecurrenceRule;
  reportFileNameTemplate: string;
  occurrences?: number;
  displayOnInvestorPortal: boolean;
  endOn?: Date;
  clientSearchFilterId: string;
  clientSearchFilter?: ClientSearchFilter;
  reportParameterId: string;
  reportParameters?: SaveReportingParametersRequest;
  emailParameterId?: string;
  emailParameters?: EmailParametersRequest;
  createdById: number;
  createdOn: Date;
  updatedById?: number;
  isDeleted: boolean;
  initiateById?: number;
  lastRunOn?: Date;
  nextRunOn?: Date;
  calculatedRemainingInstances?: number;
  calculatedFinalRunOn?: Date;
  allowTerminatedClients: boolean;
}

export type RecurrenceType = 'Once' | 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';

export interface ListScheduledEventRequest {
  page: number;
  pageSize: number;
}

export interface ClientCsvRow {
  ID?: number;
  TYPE: string;
}

export interface TriggerScheduledEventInput {
  id: string;
}

export interface ScheduledEventInput {
  name: string;
  description: string;
  recurrenceType: RecurrenceType;
  displayOnInvestorPortal: boolean;
  reportFileNameTemplate: string;
  occurrences: number;
  recurrenceRule?: RecurrenceRule;
  clientSearchFilter?: ClientSearchFilter;
  reportParameters?: SaveReportingParametersRequest;
  emailParameters?: EmailParametersRequest;
  startOn: Date;
  endOn?: Date | null;
  allowTerminatedClients: boolean;
}

export interface EmailParametersRequest {
  fromEmailAddressType: FromEmailAddressType;
  ccEmailAddressTypes: string[];
  emailAttachments: EmailAttachmentRequest[];
  emailTemplateId?: number;
  subject: string;
  body: string;
}

export interface EmailAttachmentRequest {
  attachmentId: number;
}

export interface ClientSearchFilter {
  afslIds: number[];
  practiceIds: number[];
  adviserIds: number[];
  clientTypes: string[];
  serviceType: string;
  groupIds: number[];
  clientIds: number[];
  statuses: string[];
}

export enum ReportParameterSortBy {
  NameDescending,
  NameAscending,
  CreatedOnDescending,
  CreatedOnAscending,
  ModifiedOnDescending,
  ModifiedOnAscending,
  KindDescending,
  KindAscending,
}

export interface GetReportParametersRequest {
  includeSharedAfsl?: boolean;
  kind?: string;
  sortBy?: ReportParameterSortBy;
  page?: number;
  pageSize?: number;
}

export interface GetReportParametersViewModel {
  id: string;
  name?: string;
  createdByName?: string;
  createdById: number;
  afslName?: string;
  kind?: string;
  createdOn: Date;
  modifiedOn: Date;
  visibleToAfsl: boolean;
  isSystemShared: boolean;
  canEdit: boolean;
}

export type ReportStatus = 'Not Started' | 'In Progress' | 'Completed' | 'Error';
export type ScheduledEventStatus = 'Pending Review' | 'Approved' | 'Rejected';

export type ReportParametersKind = 'C' | 'S' | 'B';

export const ReportParametersKindName: Record<ReportParametersKind, string> = {
  C: 'Client',
  S: 'Scheduler',
  B: 'Bulk',
};

export class SecurityHeld extends Enumeration {
  static Held = new SecurityHeld(1, 'Held', 'Held');
  static NotHeld = new SecurityHeld(2, 'NotHeld', "Doesn't hold");
}

export class ServiceType extends Enumeration {
  static Mda = new ServiceType(1, 'Mda', 'MDA');
  static Soa = new ServiceType(8, 'Soa', 'SOA');
  static Roa = new ServiceType(8, 'Roa', 'ROA');
}

export class ReportFileNameType extends Enumeration {
  static Merged = new ReportFileNameType(1, 'Merged', '<ClientName>_<ClientID>_Merged Reports_<GenerationDate_YYYY-MM-DD>');
  static NonMerged = new ReportFileNameType(2, 'Non-merged', '<ClientName>_<ClientID>_<ReportName>_<GenerationDate_YYYY-MM-DD>');
  static IpsPortfolio = new ReportFileNameType(3, 'IPSPortfolio', '<IPSPortfolioID>_<PortfolioName>_<AdviserCode>_<GenerationDate_DDMMYYYY>');
}

export class ClientStatusType extends Enumeration {
  static readonly Pending = new ClientStatusType(1, 'Pending', 'Pending');
  static readonly Active = new ClientStatusType(2, 'Active', 'Active');
  static readonly Terminated = new ClientStatusType(3, 'Terminated', 'Terminated');
  static readonly WaitingForApproval = new ClientStatusType(4, 'WaitingForApproval', 'Waiting for Approval');
  static readonly Stalled = new ClientStatusType(5, 'Stalled', 'Stalled');
  static readonly DatafeedActive = new ClientStatusType(6, 'DatafeedActive', 'Datafeed Active');

  static All: string[] = [
    ClientStatusType.Pending.name,
    ClientStatusType.Active.name,
    ClientStatusType.Terminated.name,
    ClientStatusType.WaitingForApproval.name,
    ClientStatusType.Stalled.name,
    ClientStatusType.DatafeedActive.name,
  ];
}

export class ClientAccountTypeForSearch extends Enumeration {
  static Company = new ClientAccountTypeForSearch(1, 'Company', 'Company');
  static Individual = new ClientAccountTypeForSearch(2, 'Individual', 'Individual');
  static Joint = new ClientAccountTypeForSearch(3, 'Joint', 'Joint');
  static IndividualPension = new ClientAccountTypeForSearch(4, 'Pension', 'Pension');
  static Smsf = new ClientAccountTypeForSearch(5, 'SMSF', 'SMSF');
  static IndividualSuper = new ClientAccountTypeForSearch(6, 'Super', 'Super');
  static Trust = new ClientAccountTypeForSearch(7, 'Trust', 'Trust');

  static getByName = (name: string): ClientAccountTypeForSearch | undefined => {
    return Object.values(ClientAccountTypeForSearch)
      .filter((t) => t instanceof ClientAccountTypeForSearch)
      .find((t: ClientAccountTypeForSearch) => t.name.toLowerCase() === name.toLowerCase());
  };

  static All: string[] = [
    ClientAccountTypeForSearch.Company.name,
    ClientAccountTypeForSearch.Individual.name,
    ClientAccountTypeForSearch.Joint.name,
    ClientAccountTypeForSearch.IndividualPension.name,
    ClientAccountTypeForSearch.Smsf.name,
    ClientAccountTypeForSearch.IndividualSuper.name,
    ClientAccountTypeForSearch.Trust.name,
  ];
}

export interface SetReportingParameterSystemShareRequest {
  reportParameterId: string;
  systemShared: boolean;
}
